/**
 * Module dependencies.
 */

import { KeyboardEvent, MouseEvent } from 'react';
import { RouterLink } from 'src/components/core/links/router-link';
import { isExternalUrl } from '@untile/react-core/utils/is-external-url';

/**
 * `OnClickType` type.
 */

type OnClickType = undefined | ((event?: MouseEvent<HTMLAnchorElement>) => void);

/**
 * Export `handleKeyboardEventProps` util.
 */

export function handleKeyboardEventProps(onClick: OnClickType) {
  if (!onClick) {
    return {};
  }

  return {
    onKeyDown: ({ code }: KeyboardEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      if (['Enter', 'Space'].includes(code)) {
        onClick();
      }
    }
  };
}

/**
 * Export `getClickProps` util.
 */

export const getClickProps = (onClick?: OnClickType) =>
  !onClick
    ? {}
    : {
        ...handleKeyboardEventProps(onClick),
        tabIndex: 0
      };

/**
 * Export `getLinkProps` util.
 */

export const getLinkProps = ({ href, target }: any) =>
  !isExternalUrl(href)
    ? {}
    : {
        rel: 'noopener',
        target: target ?? '_blank'
      };

/**
 * Export `setLinkProps` util.
 */

export function setLinkProps(props: any) {
  const { as, href, onClick } = props;
  const isExternal = href && isExternalUrl(href);
  const element = as || (href && !isExternal && RouterLink) || (href && isExternal && 'a') || 'button';

  return {
    ...getClickProps(onClick),
    ...getLinkProps(props),
    as: element
  };
}

/**
 * Export `setButtonLinkProps` util.
 */

export function setButtonLinkProps(props: any) {
  const linkProps = setLinkProps(props);

  return {
    ...linkProps,
    type: props.type || (linkProps.as === 'button' ? 'button' : null)
  };
}
